const _excluded = ["tag", "color", "className", "style", "pill", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

/**
 * Badge component that replaces reactstrap Badge to avoid defaultProps warnings.
 *
 * This custom wrapper exists because reactstrap's Badge component uses defaultProps,
 * which triggers deprecation warnings in React 18+. This component maintains full
 * API compatibility with reactstrap's Badge by:
 * - Using default parameters instead of defaultProps
 * - Spreading remaining props ({...rest}) to support all reactstrap Badge props
 * - Replicating the same className logic and structure
 *
 * @see https://react.dev/reference/react/Component#static-defaultprops
 *      React 18+ deprecates defaultProps for function components
 *
 * @param {Object} props - Component props
 * @param {string|Function} [props.tag='span'] - HTML tag or component to render
 * @param {string} [props.color='secondary'] - Badge color variant
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {Object} [props.style={}] - Inline styles
 * @param {boolean} [props.pill=false] - Whether to render as a pill-shaped badge
 * @param {React.ReactNode} [props.children=null] - Badge content
 * @param {*} props.rest - Any additional props are spread to the rendered element
 *
 * @example
 * // Drop-in replacement for reactstrap Badge
 * <Badge color="primary" pill>New</Badge>
 * <Badge tag="div" style={{ marginTop: 10 }}>Custom</Badge>
 *
 * @note This wrapper is warranted because:
 * - It's used across 8+ components in the qualityAgent module
 * - It eliminates console warnings without changing component APIs
 * - Prop spreading maintains compatibility with reactstrap's full API
 * - If reactstrap updates their Badge to remove defaultProps, this wrapper can be removed
 */
const Badge = _ref => {
  let {
      tag: Tag = 'span',
      color = 'secondary',
      className = '',
      style = {},
      pill = false,
      children = null
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const badgeClasses = classNames('badge', `badge-${color}`, {
    'badge-pill': pill
  }, className);
  return (
    /*#__PURE__*/
    // Prop spreading is intentional to maintain API compatibility with reactstrap Badge
    React.createElement(Tag, _objectSpread({
      className: badgeClasses,
      style: style
    }, rest), children)
  );
};
Badge.propTypes = {
  // Defaults are handled via default parameters in the function signature, not defaultProps
  // This avoids React 18+ deprecation warnings while maintaining API compatibility
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  // default: 'span'
  color: PropTypes.string,
  // default: 'secondary'
  className: PropTypes.string,
  // default: ''
  style: PropTypes.shape({}),
  // default: {}
  pill: PropTypes.bool,
  // default: false
  children: PropTypes.node // default: null
};
export default Badge;